import { useMemo } from 'react'

import { isInputConfig } from '../../../../application/formBuilder/config'
import { useProposal } from '../../../../application/product/proposal/ProposalProvider'
import { Data, PolicyChangeType, Profile } from '../../../../domain'
import { useAdjustments } from '../../../../state'
import { FormConfig } from '../../../../state/configuration'
import { useApiErrorHandlingAsyncCallback } from '../../../auth'
import { useErrorHandlingAsyncCallback } from '../../../errors'
import { useAdditionalProfileProposalAdjustmentRequest } from './adjustments'
import { toAdditionalProfileProposalData } from './data'

export const useAdditionalProfileFormData = (
  form: FormConfig,
  profile: Profile | undefined,
) => {
  const { getAdjustment } = useAdjustments()

  return useMemo(() => {
    if (!profile) return {}

    const toData = toAdditionalProfileProposalData(profile)

    return form.filter(isInputConfig).reduce((data, input) => {
      const adjustment = getAdjustment(input.name)

      return {
        ...data,
        [input.name]: toData(adjustment),
      }
    }, {})
  }, [form, profile, getAdjustment])
}

export const useRemoveAdditionalProfileProposalAdjustment = (applyRating: boolean) => {
  const { commit } = useProposal()

  return useErrorHandlingAsyncCallback(async (profile: Profile) => {
    await commit({
      applyRating,
      changes: [
        {
          changeType: PolicyChangeType.REMOVE_PROFILE,
          profileCode: profile.profileCode,
        },
      ],
    })
  })
}

interface AdditionalProfileProposalAdjustmentProps {
  form: FormConfig
  applyRating: boolean
  profile: Profile
  onComplete?: () => void | Promise<void>
}

export const useAdditionalProfileProposalAdjustment = ({
  form,
  applyRating,
  profile,
  onComplete,
}: AdditionalProfileProposalAdjustmentProps) => {
  const { commit } = useProposal()

  const existingData = useAdditionalProfileFormData(form, profile)
  const proposalAdjustmentRequest = useAdditionalProfileProposalAdjustmentRequest(
    form,
    applyRating,
    profile,
  )

  return useApiErrorHandlingAsyncCallback(async (data: Data) => {
    await commit(proposalAdjustmentRequest(existingData, data))
    await onComplete?.()
  })
}
